/**
 * Vendors on a project task. Up to three snapshots live on the task JSON
 * (projects.tasks), including a name and phone so the homeowner can see
 * them without reading the vendors table. A single `vendor` field remains
 * the first snapshot for older screens.
 */

const { MAX_TASK_VENDORS, VENDOR_LIMIT_MESSAGE } = require('./projectTaskLimits');

function clean(value) {
  if (value == null) return '';
  return String(value).trim();
}

function vendorOption(vendor, source) {
  const row = vendor || {};
  const name = clean(row.name);
  const region = clean(row.region);
  const labelName = name || 'Vendor';
  return {
    id: row.id,
    source,
    name,
    phone: clean(row.phone),
    label: source === 'regional' && region ? `${labelName} (${region})` : labelName,
  };
}

function taskVendorOptions(agentVendors, regionalVendors) {
  const agent = (agentVendors || [])
    .filter((vendor) => vendor && vendor.id)
    .map((vendor) => vendorOption(vendor, 'agent'));
  const regional = (regionalVendors || [])
    .filter((vendor) => vendor && vendor.id && vendor.is_active !== false)
    .map((vendor) => vendorOption(vendor, 'regional'));
  return { agent, regional };
}

function taskVendorValue(task) {
  const row = task || {};
  if (row.vendor && typeof row.vendor === 'object' && row.vendor.id) {
    return `${row.vendor.source || 'agent'}:${row.vendor.id}`;
  }
  if (typeof row.vendor === 'string' && clean(row.vendor)) {
    const raw = clean(row.vendor);
    return raw.includes(':') ? raw : `agent:${raw}`;
  }
  const ids = Array.isArray(row.vendor_ids) ? row.vendor_ids : [];
  if (ids[0]) return `agent:${ids[0]}`;
  return '';
}

function snapshotFrom(option) {
  const row = option || {};
  return {
    id: row.id,
    source: row.source || 'agent',
    name: clean(row.name),
    phone: clean(row.phone),
  };
}

function withVendors(task, vendors) {
  const row = task || {};
  const list = (vendors || []).slice(0, MAX_TASK_VENDORS);
  return {
    ...row,
    vendors: list,
    vendor_ids: list.map((vendor) => vendor.id).filter(Boolean),
    vendor: list[0] || null,
  };
}

function savedVendorName(value) {
  const name = realVendorName(value);
  if (!name || name === 'Vendor') return '';
  return name;
}

function resolveVendorName(savedName, loaded) {
  const known = realVendorName(loaded && loaded.name);
  return known || savedVendorName(savedName) || '';
}

function vendorRefs(task) {
  const row = task || {};
  const refs = [];
  const push = (entry) => {
    const source = (entry && entry.source) || 'agent';
    const id = entry && entry.id ? String(entry.id) : '';
    const name = entry && entry.name != null ? String(entry.name) : '';
    const phone = clean(entry && entry.phone);
    if (!id && !savedVendorName(name)) return;
    const key = id ? `${source}:${id}` : `name:${clean(name)}`;
    if (refs.some((item) => item.key === key)) return;
    refs.push({ id, source, name, phone, key });
  };
  if (Array.isArray(row.vendors) && row.vendors.length) {
    row.vendors.forEach((vendor) => {
      if (vendor && typeof vendor === 'object') push(vendor);
    });
  } else {
    if (row.vendor && typeof row.vendor === 'object') push(row.vendor);
    else if (typeof row.vendor === 'string' && clean(row.vendor)) {
      const raw = clean(row.vendor);
      const split = raw.split(':');
      const hasSource = split.length > 1 && /^(agent|regional|team|homeowner)$/i.test(split[0]);
      push({
        id: hasSource ? split.slice(1).join(':') : raw,
        source: hasSource ? split[0].toLowerCase() : 'agent',
      });
    }
    const ids = Array.isArray(row.vendor_ids) ? row.vendor_ids : [];
    ids.forEach((id) => {
      if (id) push({ id, source: 'agent' });
    });
  }
  return refs.slice(0, MAX_TASK_VENDORS);
}

function taskVendorList(task, loadedVendors) {
  return vendorRefs(task).map((ref) => {
    const known = knownVendor(loadedVendors, ref.id);
    return snapshotFrom({
      id: ref.id,
      source: ref.source,
      name: resolveVendorName(ref.name, known),
      phone: clean((known && known.phone) || ref.phone),
    });
  });
}

function openVendorOptions(options, attached) {
  const taken = {};
  (attached || []).forEach((vendor) => {
    if (vendor && vendor.id) taken[`${vendor.source || 'agent'}:${vendor.id}`] = true;
  });
  return (options || []).filter((option) => option && option.value && !taken[option.value]);
}

function sameVendor(left, right) {
  return String(left && left.id) === String(right && right.id)
    && (left.source || 'agent') === (right.source || 'agent');
}

function addTaskVendor(task, option, loadedVendors) {
  const current = taskVendorList(task, loadedVendors);
  if (!option || !option.id) return { task: withVendors(task, current), error: '' };
  const next = snapshotFrom(option);
  if (current.some((vendor) => sameVendor(vendor, next))) {
    return { task: withVendors(task, current), error: '' };
  }
  if (current.length >= MAX_TASK_VENDORS) {
    return { task: withVendors(task, current), error: VENDOR_LIMIT_MESSAGE };
  }
  return { task: withVendors(task, current.concat([next])), error: '' };
}

function removeTaskVendor(task, source, id, loadedVendors) {
  const next = taskVendorList(task, loadedVendors).filter((vendor) => !sameVendor(vendor, { id, source: source || 'agent' }));
  return withVendors(task, next);
}

function applyTaskVendor(task, option) {
  if (!option || !option.id) return withVendors(task, []);
  return withVendors(task, [snapshotFrom(option)]);
}

const ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function realVendorName(value) {
  const name = clean(value);
  if (!name || ID_RE.test(name)) return '';
  if (/^(agent|regional|team):/i.test(name)) return '';
  return name;
}

function referencedVendorId(task) {
  const row = task || {};
  if (typeof row.vendor === 'string') {
    const raw = clean(row.vendor);
    if (!raw) return '';
    const split = raw.split(':');
    return split.length > 1 ? split.slice(1).join(':') : raw;
  }
  if (row.vendor && typeof row.vendor === 'object' && row.vendor.id) return String(row.vendor.id);
  const ids = Array.isArray(row.vendor_ids) ? row.vendor_ids : [];
  return ids[0] ? String(ids[0]) : '';
}

function knownVendor(loadedVendors, id) {
  if (!id) return null;
  return (loadedVendors || []).find((vendor) => vendor && String(vendor.id) === String(id)) || null;
}

function missingTaskVendor(task, options, knownVendors) {
  const value = taskVendorValue(task);
  if (!value) return null;
  const lists = options || {};
  const choices = [...(lists.agent || []), ...(lists.regional || [])];
  const choice = choices.find((vendor) => `${vendor.source}:${vendor.id}` === value);
  if (choice && realVendorName(choice.name)) return null;
  const saved = taskVendorDisplay(task, knownVendors);
  if (saved && saved.name) return { name: saved.name };
  return { name: 'Vendor' };
}

function taskVendorDisplay(task, loadedVendors) {
  const row = task || {};
  const id = referencedVendorId(row);
  const known = knownVendor(loadedVendors, id);
  const snapshotName = row.vendor && typeof row.vendor === 'object' ? row.vendor.name : '';
  const name = resolveVendorName(snapshotName, known);
  if (!name) return null;
  const knownPhone = known ? clean(known.phone) : '';
  const snapshotPhone = row.vendor && typeof row.vendor === 'object' ? clean(row.vendor.phone) : '';
  return { name, phone: knownPhone || snapshotPhone };
}

export {
  MAX_TASK_VENDORS,
  VENDOR_LIMIT_MESSAGE,
  taskVendorOptions,
  taskVendorValue,
  taskVendorList,
  openVendorOptions,
  addTaskVendor,
  removeTaskVendor,
  applyTaskVendor,
  taskVendorDisplay,
  missingTaskVendor,
};
